import {
  ArrowPathIcon,
  ArrowUpIcon,
  ArrowsUpDownIcon,
  DocumentArrowDownIcon,
  ListBulletIcon,
  SparklesIcon,
  VideoCameraIcon,
  ViewColumnsIcon,
} from "@heroicons/react/24/outline";
import pako from "pako";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DoublesAnalysisTables } from "../components/DoublesAnalysisSection";
import { allFilters } from "../components/filters/AllFilters";
import TennisCourt from "../components/filters/TennisCourt";
import { useAuthStatus } from "../hooks/useAuthStatus";
import ComparisonBars from "../utils/ComparisonBars";
import { fetchMatchWithData } from "../utils/dbutils";
import { computeDoublesAnalysis } from "../utils/doublesAnalysis";
import { updateStats } from "../utils/StatsObject";
import { PlaylistViewer } from "./Playlists";

// ── PSPL helpers ─────────────────────────────────────────────────────────────

function compressToPspl(str) {
  const compressed = pako.deflate(str);
  let binary = "";
  for (let i = 0; i < compressed.length; i++)
    binary += String.fromCharCode(compressed[i]);
  return btoa(binary);
}

/** Build a temporary playlist from raw TennisCourt filtered events */
function buildPlaylistPspl(title, events, matches) {
  // Map every event object (by reference) → its parent match + set
  const evMap = new Map();
  for (const match of matches) {
    for (const set of match.sets ?? []) {
      for (const ev of set.events ?? []) {
        evMap.set(ev, { match, set });
      }
    }
  }

  let str = `${title}[0][0]${new Date().toISOString()}\n`;
  for (const event of events) {
    const entry = evMap.get(event);
    const match = entry?.match;
    const set = entry?.set;

    let playerName = "Unknown";
    if (match?.isDoubles) {
      const allPlayers = [
        match.teamA?.player1, match.teamA?.player2,
        match.teamB?.player1, match.teamB?.player2,
      ];
      const found = allPlayers.find((p) => p?.guid === event.playerGuid);
      if (found) {
        playerName = `${found.firstName ?? ""} ${found.lastName ?? ""}`.trim() || "Unknown";
      } else {
        playerName = (event.isPlayer1 ? match.teamA?.name : match.teamB?.name) ?? "Unknown";
      }
    } else {
      const firstName = event.isPlayer1
        ? (match?.player1?.firstName ?? match?.player1FirstName ?? "")
        : (match?.player2?.firstName ?? match?.player2FirstName ?? "");
      const lastName = event.isPlayer1
        ? (match?.player1?.lastName ?? match?.player1LastName ?? "")
        : (match?.player2?.lastName ?? match?.player2LastName ?? "");
      playerName = `${firstName} ${lastName}`.trim() || "Unknown";
    }

    const desc = event.eventString ?? "";
    const videoUrl = match?.videoFile ?? "";
    const videoPos = event.videoTime != null ? Math.max(0, event.videoTime - 3) : 0;

    const scoreParts = (event.realScores ?? "").split(" ");
    const setScore = (scoreParts[0] ?? "").replace(":", "-");
    const p1Name = [match?.player1FirstName, match?.player1LastName].filter(Boolean).join(" ");
    const p2Name = [match?.player2FirstName, match?.player2LastName].filter(Boolean).join(" ");
    const matchName = p1Name && p2Name ? `${p1Name} vs ${p2Name}` : "";
    const subString = `[S${set?.number ?? event.setNumber ?? ""} ${setScore}] - ${matchName}`;

    str += `${playerName}[0]${desc}[0]${videoUrl}[0]${videoPos}[0][0]${subString}[0][0][0][0][0]\n`;
  }
  return str;
}

// ── Doubles helper ──────────────────────────────────────────────────────────

/** Returns true if the focus player/team is on side A (player1 / teamA). */
function isFocusTeamA(m, playerGuid) {
  if (!playerGuid) return true;
  if (!m) return true;
  if (!m.isDoubles) return m.player1Guid === playerGuid;
  const teamAGuid =
    m.teamA?.guid ||
    (m.teamA?.player1?.guid && m.teamA?.player2?.guid
      ? `${m.teamA.player1.guid}_${m.teamA.player2.guid}`
      : null);
  return (
    teamAGuid === playerGuid ||
    m.teamA?.player1?.guid === playerGuid ||
    m.teamA?.player2?.guid === playerGuid
  );
}

/** Resolve display name from a player or team object. */
function resolveDisplayName(p) {
  if (!p) return null;
  if (p.name) return p.name;
  return `${p.firstName ?? ""} ${p.lastName?.toUpperCase() ?? ""}`.trim();
}

/** Resolve initials from a player or team object. */
function resolveInitials(p) {
  if (!p) return "?";
  if (p.name) return p.name.split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 2);
  return `${p.firstName?.[0] ?? ""}${p.lastName?.[0] ?? ""}`.toUpperCase();
}

// ── Filter builder ───────────────────────────────────────────────────────────

function buildFilters({ shots = null, scores = null, hand = null, outcomeWinnersOnly = false } = {}) {
  const opts = JSON.parse(JSON.stringify(allFilters));

  if (shots) {
    const f = opts.find((o) => o.title === "Shots");
    f.items.forEach((item) => { item.selected = shots.includes(item.name); });
  }
  if (scores) {
    const f = opts.find((o) => o.title === "Scores");
    f.items.forEach((item) => { item.selected = scores.includes(item.name); });
  }
  if (outcomeWinnersOnly) {
    const f = opts.find((o) => o.title === "Outcome");
    f.items.forEach((item) => {
      item.selected = ["Winner", "Forced Error", "Unforced Error"].includes(item.name);
    });
  }
  if (hand === "forehand") {
    const f = opts.find((o) => o.title === "Backhand");
    f.items.forEach((item) => { item.selected = false; });
  } else if (hand === "backhand") {
    const f = opts.find((o) => o.title === "Forehand");
    f.items.forEach((item) => { item.selected = false; });
  }
  return opts;
}

// ── Report section definitions ───────────────────────────────────────────────

const REPORT_SECTIONS = [
  {
    id: 2,
    title: "First Serves",
    filters: buildFilters({ shots: ["1st Serve Deuce", "1st Serve Ad"] }),
  },
  {
    id: 3,
    title: "Second Serves",
    filters: buildFilters({ shots: ["2nd Serve Deuce", "2nd Serve Ad"] }),
  },
  {
    id: 4,
    title: "First Serves On Break Points",
    filters: buildFilters({ shots: ["1st Serve Deuce", "1st Serve Ad"], scores: ["BP Against"] }),
  },
  {
    id: 5,
    title: "Second Serves On Break Points",
    filters: buildFilters({ shots: ["2nd Serve Deuce", "2nd Serve Ad"], scores: ["BP Against"] }),
  },
  {
    id: 6,
    title: "Returns On 1st Deuce Serves",
    filters: buildFilters({ shots: ["Return 1st Deuce"] }),
  },
  {
    id: 7,
    title: "Returns On 1st Ad Serves",
    filters: buildFilters({ shots: ["Return 1st Ad"] }),
  },
  {
    id: 8,
    title: "Returns On 1st Serves Forehand",
    filters: buildFilters({ shots: ["Return 1st Deuce", "Return 1st Ad"], hand: "forehand" }),
  },
  {
    id: 9,
    title: "Returns On 1st Serves Backhand",
    filters: buildFilters({ shots: ["Return 1st Deuce", "Return 1st Ad"], hand: "backhand" }),
  },
  {
    id: 10,
    title: "Returns On 2nd Deuce Serves",
    filters: buildFilters({ shots: ["Return 2nd Deuce"] }),
  },
  {
    id: 11,
    title: "Returns On 2nd Ad Serves",
    filters: buildFilters({ shots: ["Return 2nd Ad"] }),
  },
  {
    id: 12,
    title: "Returns On 2nd Serves Forehand",
    filters: buildFilters({ shots: ["Return 2nd Deuce", "Return 2nd Ad"], hand: "forehand" }),
  },
  {
    id: 13,
    title: "Returns On 2nd Serves Backhand",
    filters: buildFilters({ shots: ["Return 2nd Deuce", "Return 2nd Ad"], hand: "backhand" }),
  },
  {
    id: 14,
    title: "Key Points",
    filters: buildFilters({ shots: ["Keypoint Shot"], outcomeWinnersOnly: true }),
  },
  {
    id: 15,
    title: "Key Points Forehand",
    filters: buildFilters({ shots: ["Keypoint Shot"], outcomeWinnersOnly: true, hand: "forehand" }),
  },
  {
    id: 16,
    title: "Key Points Backhand",
    filters: buildFilters({ shots: ["Keypoint Shot"], outcomeWinnersOnly: true, hand: "backhand" }),
  },
  {
    id: 17,
    title: "Serve Volleys",
    filters: buildFilters({ shots: ["Serve Volleys"], outcomeWinnersOnly: true }),
  },
  {
    id: 18,
    title: "Net Approaches",
    filters: buildFilters({ shots: ["Approach Shots"], outcomeWinnersOnly: true }),
  },
];

const DISPLAY_MODES = [
  { value: 0, label: "Ball Path" },
  { value: 1, label: "Heatmap" },
  { value: 2, label: "Zone Count" },
  { value: 3, label: "Zone Percentage" },
];

const STAT_CATEGORIES = [
  "Points",
  "Serving",
  "Returning",
  "Break Points",
  "Rally Length",
  "Positioning",
];

// ── CourtSection ─────────────────────────────────────────────────────────────

function CourtSection({ title, filters, matches, playerGuid, displayMode, isNormalised, showSource, onOpenPlaylist, printMode = false }) {
  const [filteredEvents, setFilteredEvents] = useState([]);

  const handleEventsChanged = useCallback((evs) => {
    setFilteredEvents(evs ?? []);
  }, []);

  const handlePlaylistClick = useCallback(() => {
    if (filteredEvents.length === 0) return;
    const psplStr = buildPlaylistPspl(title, filteredEvents, matches);
    const data = compressToPspl(psplStr);
    onOpenPlaylist({ data, name: title, comments: {}, firestoreId: null });
  }, [filteredEvents, matches, title, onOpenPlaylist]);

  // Court aspect ratio: standard court ~ 10.97m wide, 23.77m long ≈ 0.46 aspect (w/h)
  // Use a padding-top trick so the court fills the full width naturally
  return (
    <div className="border border-base-300 rounded-lg overflow-hidden" data-pdf-section>
      {/* Header */}
      <div className="px-3 py-2 bg-base-200 border-b border-base-300 flex items-center gap-2">
        <h3 className="font-semibold text-sm flex-1">{title}</h3>
        <span className="badge badge-sm badge-neutral">{filteredEvents.length} events</span>
        <button
          className="btn btn-xs btn-ghost"
          title={filteredEvents.length === 0 ? "No events to show" : "Open playlist viewer"}
          disabled={filteredEvents.length === 0}
          onClick={handlePlaylistClick}
        >
          <VideoCameraIcon className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* Court — fills full width, auto height via aspect ratio container */}
      <div className="relative w-full" style={{ paddingTop: "180%" }}>
        <div className="absolute inset-0">
          {matches.length > 0 ? (
            <TennisCourt
              sets={[]}
              matches={matches}
              allOptions={filters}
              playerGuid={playerGuid}
              oppositionId={null}
              displayMode={displayMode}
              isNormalised={isNormalised}
              showSource={showSource}
              handleFilteredEventsChanged={handleEventsChanged}
              printMode={printMode}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-base-content/30 text-sm">
              No data
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Stats helpers (mirrored from StatsPage) ──────────────────────────────────

function mergeStats(a, b) {
  if (!a) return { ...b };
  if (!b) return { ...a };
  const result = { ...a };
  for (const key of Object.keys(b)) {
    if (Array.isArray(b[key])) {
      result[key] = [...(a[key] ?? []), ...b[key]];
    } else if (typeof b[key] === "number") {
      result[key] = (a[key] ?? 0) + b[key];
    }
  }
  return result;
}

// Stats for ONE individual within a doubles team. Narrows StatsObject.js'
// event-bucketing (soPlayer vs soOpposition) down to just this individual's
// own events via updateStats' 3rd arg — team win/breakpoint/rally detection
// still uses the REAL match.teamA/teamB (never substituted), since those are
// inherently team-level. With individualGuid set, isTeamMember ignores the
// `team` argument entirely and buckets purely by guid match — so the target
// individual's own events ALWAYS land in soPlayer/`.statsA`, regardless of
// whether they're really on match.teamA or match.teamB. (`team` param kept
// only for the `!team` guard below; do NOT use it to pick statsA vs statsB.)
function individualStatsForPlayer(m, team, individual) {
  if (!team || !individual) return null;
  return updateStats(m, 0, individual.guid).statsA;
}

function buildStatBars(statsA, statsB) {
  var idx = 1;
  var aw1 = [...(statsA.acesEvents ?? []), ...(statsA.serveWinnersEvents ?? [])];
  var aw2 = [...(statsB.acesEvents ?? []), ...(statsB.serveWinnersEvents ?? [])];
  var fe1 = [...(statsA.forehandForcingErrorsEvents ?? []), ...(statsA.backhandForcingErrorsEvents ?? [])];
  var fe2 = [...(statsB.forehandForcingErrorsEvents ?? []), ...(statsB.backhandForcingErrorsEvents ?? [])];
  var ue1 = [...(statsA.forehandUnforcedErrorsEvents ?? []), ...(statsA.backhandUnforcedErrorsEvents ?? [])];
  var ue2 = [...(statsB.forehandUnforcedErrorsEvents ?? []), ...(statsB.backhandUnforcedErrorsEvents ?? [])];
  var w1 = [...(statsA.totalWinnersEvents ?? [])];
  var w2 = [...(statsB.totalWinnersEvents ?? [])];
  var tp1 = [...(statsA.totalWinnersEvents ?? []), ...fe2, ...ue2];
  var tp2 = [...(statsB.totalWinnersEvents ?? []), ...fe1, ...ue1];
  var tpp1 = [...(statsA.totalPressurePointsWonEvents ?? [])];
  var tpp2 = [...(statsB.totalPressurePointsWonEvents ?? [])];

  return [
    { id: idx++, value1: tp1.length, value2: tp2.length, label: "Total Points Won", category: 0, events1: tp1.sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1)), events2: tp2.sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1)) },
    { id: idx++, value1: tpp1.length, value2: tpp2.length, label: "Total Pressure Points Won", category: 0, events1: tpp1, events2: tpp2 },
    { id: idx++, value1: statsA.totalWinners ?? 0, value2: statsB.totalWinners ?? 0, label: "Winners", category: 0, events1: w1, events2: w2 },
    { id: idx++, value1: (statsA.backhandUnforcedErrors ?? 0) + (statsA.forehandUnforcedErrors ?? 0), value2: (statsB.backhandUnforcedErrors ?? 0) + (statsB.forehandUnforcedErrors ?? 0), label: "Unforced Errors", category: 0, events1: ue1, events2: ue2 },
    { id: idx++, value1: (statsA.backhandForcingErrors ?? 0) + (statsA.forehandForcingErrors ?? 0), value2: (statsB.backhandForcingErrors ?? 0) + (statsB.forehandForcingErrors ?? 0), label: "Forced Errors", category: 0, events1: fe1, events2: fe2 },
    { id: idx++, value1: (statsA.aces ?? 0) + (statsA.serveWinners ?? 0), value2: (statsB.aces ?? 0) + (statsB.serveWinners ?? 0), label: "Serve Aces/Winners", category: 1, events1: aw1, events2: aw2 },
    { id: idx++, value1: statsA.doublefaults ?? 0, value2: statsB.doublefaults ?? 0, label: "Double Faults", category: 1, events1: statsA.doublefaultsEvents ?? [], events2: statsB.doublefaultsEvents ?? [] },
    { id: idx++, value1: ((statsA.firstServeIns * 100) / (statsA.firstServes || 1)).toFixed(0), value2: ((statsB.firstServeIns * 100) / (statsB.firstServes || 1)).toFixed(0), label: "First Serves In", category: 1, isPercent: true, num1: statsA.firstServeIns ?? 0, den1: statsA.firstServes ?? 0, num2: statsB.firstServeIns ?? 0, den2: statsB.firstServes ?? 0, events1: statsA.firstServeInsEvents ?? [], events2: statsB.firstServeInsEvents ?? [] },
    { id: idx++, value1: ((statsA.firstServicePointsWon * 100) / (statsA.firstServeIns || 1)).toFixed(0), value2: ((statsB.firstServicePointsWon * 100) / (statsB.firstServeIns || 1)).toFixed(0), label: "First Serves Won", category: 1, isPercent: true, num1: statsA.firstServicePointsWon ?? 0, den1: statsA.firstServeIns ?? 0, num2: statsB.firstServicePointsWon ?? 0, den2: statsB.firstServeIns ?? 0, events1: statsA.firstServicePointsWonRallies ?? [], events2: statsB.firstServicePointsWonRallies ?? [] },
    { id: idx++, value1: ((statsA.secondServicePointsWon * 100) / (statsA.secondServeIns || 1)).toFixed(0), value2: ((statsB.secondServicePointsWon * 100) / (statsB.secondServeIns || 1)).toFixed(0), label: "Second Serves Won", category: 1, isPercent: true, num1: statsA.secondServicePointsWon ?? 0, den1: statsA.secondServeIns ?? 0, num2: statsB.secondServicePointsWon ?? 0, den2: statsB.secondServeIns ?? 0, events1: statsA.secondServicePointsWonRallies ?? [], events2: statsB.secondServicePointsWonRallies ?? [] },
    { id: idx++, value1: !statsA.returnsFirstServe ? 0 : ((statsA.pointsWonOnReturnOffFirstServes * 100) / (statsA.returnsFirstServe || 1)).toFixed(0), value2: !statsB.returnsFirstServe ? 0 : ((statsB.pointsWonOnReturnOffFirstServes * 100) / (statsB.returnsFirstServe || 1)).toFixed(0), label: "1st Serve Return Points Won", category: 2, isPercent: true, num1: statsA.pointsWonOnReturnOffFirstServes ?? 0, den1: statsA.returnsFirstServe ?? 0, num2: statsB.pointsWonOnReturnOffFirstServes ?? 0, den2: statsB.returnsFirstServe ?? 0, events1: statsA.pointsWonOnReturnOffFirstServesRallies ?? [], events2: statsB.pointsWonOnReturnOffFirstServesRallies ?? [] },
    { id: idx++, value1: !statsA.returnsSecondServe ? 0 : ((statsA.pointsWonOnReturnOffSecondServes * 100) / (statsA.returnsSecondServe || 1)).toFixed(0), value2: !statsB.returnsSecondServe ? 0 : ((statsB.pointsWonOnReturnOffSecondServes * 100) / (statsB.returnsSecondServe || 1)).toFixed(0), label: "2nd Serve Return Points Won", category: 2, isPercent: true, num1: statsA.pointsWonOnReturnOffSecondServes ?? 0, den1: statsA.returnsSecondServe ?? 0, num2: statsB.pointsWonOnReturnOffSecondServes ?? 0, den2: statsB.returnsSecondServe ?? 0, events1: statsA.pointsWonOnReturnOffSecondServesRallies ?? [], events2: statsB.pointsWonOnReturnOffSecondServesRallies ?? [] },
    { id: idx++, value1: `${statsA.breakPointsConverted ?? 0} / ${statsA.breakPoints ?? 0}`, value2: `${statsB.breakPointsConverted ?? 0} / ${statsB.breakPoints ?? 0}`, label: "Break Points Won", category: 3, events1: statsA.breakPointsConvertedRallies ?? [], events2: statsB.breakPointsConvertedRallies ?? [] },
    { id: idx++, value1: statsA.totalShortRalliesWon ?? 0, value2: statsB.totalShortRalliesWon ?? 0, label: "Short Rallies (0-4) Won", category: 4, events1: statsA.totalShortRalliesWonRallies ?? [], events2: statsB.totalShortRalliesWonRallies ?? [] },
    { id: idx++, value1: statsA.totalMediumRalliesWon ?? 0, value2: statsB.totalMediumRalliesWon ?? 0, label: "Medium Rallies (5-8) Won", category: 4, events1: statsA.totalMediumRalliesWonRallies ?? [], events2: statsB.totalMediumRalliesWonRallies ?? [] },
    { id: idx++, value1: statsA.totalLongRalliesWon ?? 0, value2: statsB.totalLongRalliesWon ?? 0, label: "Long Rallies (9+) Won", category: 4, events1: statsA.totalLongRalliesWonRallies ?? [], events2: statsB.totalLongRalliesWonRallies ?? [] },
    { id: idx++, value1: statsA.totalShotsWithin2mOfBaseLine ?? 0, value2: statsB.totalShotsWithin2mOfBaseLine ?? 0, label: "Shots Within 2m of Baseline", category: 5, events1: statsA.totalShotsWithin2mOfBaseLineEvents ?? [], events2: statsB.totalShotsWithin2mOfBaseLineEvents ?? [] },
  ];
}

// ── GeneralReport ─────────────────────────────────────────────────────────────

const DEFAULT_LEFT_WIDTH = 350;
const MIN_LEFT_WIDTH = 220;
const MAX_LEFT_WIDTH = 700;

function GeneralReport() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const matchGuids = state?.matchGuids ?? (state?.match?.guid ? [state.match.guid] : []);
  const playerGuid = state?.playerGuid ?? state?.match?.player1Guid ?? null;

  const { firebaseUser } = useAuthStatus();
  const uid = firebaseUser?.uid ?? null;

  const [matches, setMatches] = useState([]);
  const [statBars, setStatBars] = useState([]);
  // Doubles-only individual breakdowns: focus team's 2 players always;
  // opposition team's 2 players only when viewing exactly one match.
  const [focusIndividualBars, setFocusIndividualBars] = useState([]);
  const [oppIndividualBars, setOppIndividualBars] = useState([]);
  // Doubles-only: formation/serve-direction/return-hand breakdown, computed
  // straight from the loaded matches (cheap enough not to need its own effect).
  const doublesAnalysis = useMemo(
    () => computeDoublesAnalysis(matches, playerGuid),
    [matches, playerGuid],
  );
  // Independent breakdown-mode selection per category (all default to the
  // plain aggregated view) — Net Position has no breakdown, see doublesAnalysis.js.
  const [serveFormationMode, setServeFormationMode] = useState("combined");
  const [serveDirectionMode, setServeDirectionMode] = useState("combined");
  const [returnFormationMode, setReturnFormationMode] = useState("combined");
  const [returnHandMode, setReturnHandMode] = useState("combined");
  const [loading, setLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [showMatchList, setShowMatchList] = useState(false);
  const [showPanel, setShowPanel] = useState(true); // show/hide left report panel

  // Global display options
  const [isNormalised, setIsNormalised] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const [displayMode, setDisplayMode] = useState(0);

  // Two-column layout
  const [leftWidth, setLeftWidth] = useState(DEFAULT_LEFT_WIDTH);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(DEFAULT_LEFT_WIDTH);
  const bodyRef = useRef(null);

  // Active playlist in right panel
  const [activePlaylist, setActivePlaylist] = useState(null);

  const reportRef = useRef(null);

  // ── Resize handle ─────────────────────────────────────────────────────────

  const onDragStart = useCallback((e) => {
    dragging.current = true;
    startX.current = e.clientX;
    startW.current = leftWidth;
    e.preventDefault();
  }, [leftWidth]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const delta = e.clientX - startX.current;
      setLeftWidth(Math.min(MAX_LEFT_WIDTH, Math.max(MIN_LEFT_WIDTH, startW.current + delta)));
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // ── Fetch match data ─────────────────────────────────────────────────────

  useEffect(() => {
    if (matchGuids.length === 0 || !uid) return;
    setLoading(true);

    const run = async () => {
      const fetched = (
        await Promise.all(matchGuids.map((g) => fetchMatchWithData(uid, g)))
      ).filter(Boolean);
      setMatches(fetched);

      let mergedA = null;
      let mergedB = null;
      let mergedFocusP1 = null;
      let mergedFocusP2 = null;
      let mergedOppP1 = null;
      let mergedOppP2 = null;
      const singleMatch = fetched.length === 1;
      for (const m of fetched) {
        const ret = updateStats(m, 0);
        const focusIsP1 = isFocusTeamA(m, playerGuid);
        const sA = focusIsP1 ? ret.statsA : ret.statsB;
        const sB = focusIsP1 ? ret.statsB : ret.statsA;
        mergedA = mergeStats(mergedA, sA);
        mergedB = mergeStats(mergedB, sB);

        if (m.isDoubles) {
          const focusTeam = focusIsP1 ? m.teamA : m.teamB;
          const oppTeam = focusIsP1 ? m.teamB : m.teamA;
          if (focusTeam?.player1) mergedFocusP1 = mergeStats(mergedFocusP1, individualStatsForPlayer(m, focusTeam, focusTeam.player1));
          if (focusTeam?.player2) mergedFocusP2 = mergeStats(mergedFocusP2, individualStatsForPlayer(m, focusTeam, focusTeam.player2));
          if (singleMatch) {
            if (oppTeam?.player1) mergedOppP1 = mergeStats(mergedOppP1, individualStatsForPlayer(m, oppTeam, oppTeam.player1));
            if (oppTeam?.player2) mergedOppP2 = mergeStats(mergedOppP2, individualStatsForPlayer(m, oppTeam, oppTeam.player2));
          }
        }
      }
      if (mergedA && mergedB) setStatBars(buildStatBars(mergedA, mergedB));
      setFocusIndividualBars(mergedFocusP1 && mergedFocusP2 ? buildStatBars(mergedFocusP1, mergedFocusP2) : []);
      setOppIndividualBars(singleMatch && mergedOppP1 && mergedOppP2 ? buildStatBars(mergedOppP1, mergedOppP2) : []);
    };

    run().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchGuids.join(","), uid]);

  // ── Player header ────────────────────────────────────────────────────────

  const renderPlayerHeader = () => {
    const firstMatch = matches[0];
    if (!firstMatch) return null;
    const focusIsP1 = isFocusTeamA(firstMatch, playerGuid);
    const focusPlayer = firstMatch.isDoubles
      ? (focusIsP1 ? firstMatch.teamA : firstMatch.teamB)
      : (focusIsP1 ? firstMatch.player1 : firstMatch.player2);
    const oppPlayer = matches.length === 1
      ? (firstMatch.isDoubles
        ? (focusIsP1 ? firstMatch.teamB : firstMatch.teamA)
        : (focusIsP1 ? firstMatch.player2 : firstMatch.player1))
      : null;

    const renderAvatar = (p, fallback) => {
      if (!p) {
        return (
          <div className="flex flex-col items-center gap-1">
            <div className="w-8 h-8 rounded-full bg-base-300 flex items-center justify-center text-sm font-bold text-base-content/40">?</div>
            <span className="text-xs font-semibold text-base-content/50">{fallback}</span>
          </div>
        );
      }
      const initials = resolveInitials(p);
      const displayName = resolveDisplayName(p);
      return (
        <div className="flex flex-col items-center gap-1">
          {p.photoUrl ? (
            <img src={p.photoUrl} alt={initials} className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-primary text-primary-content flex items-center justify-center text-sm font-bold">
              {initials}
            </div>
          )}
          <span className="text-xs font-semibold">{displayName}</span>
        </div>
      );
    };

    return (
      <div className="flex items-center justify-center gap-6 py-3">
        {renderAvatar(focusPlayer, "Focus Player")}
        <span className="text-base-content/30 font-bold text-sm">vs</span>
        {renderAvatar(oppPlayer, matches.length > 1 ? `${matches.length} Opponents` : "Opponent")}
      </div>
    );
  };

  // Doubles-only: renders 2 compact rows right under a stat's ComparisonBars
  // — one per player pair — e.g. "NADAL 10 20 DJOKOVIC" / "FEDERER 14 10
  // MURRAY". `oppRow`/name3/name4 are omitted (blank) unless viewing a
  // single match, matching focusIndividualBars/oppIndividualBars.
  const renderPlayerBreakdownRow = (focusRow, oppRow, names, onFocusClicked, onOppClicked) => {
    if (!focusRow) return null;
    const [name1, name2, name3, name4] = names;
    const fmt = (row, side) => {
      if (!row) return "";
      const val = side === 1 ? row.value1 : row.value2;
      const num = side === 1 ? row.num1 : row.num2;
      const den = side === 1 ? row.den1 : row.den2;
      if (!row.isPercent) return `${val}`;
      return den !== undefined ? `(${num ?? 0}/${den}) ${val}%` : `${val}%`;
    };
    const cell = (label, row, side, onClicked) => (
      <span
        className={row?.events1 || row?.events2 ? "cursor-pointer hover:underline" : ""}
        onClick={() => row && onClicked?.((side === 1 ? row.events1 : row.events2) ?? [], row, side)}
      >
        {label}
      </span>
    );
    return (
      <div className="text-[11px] text-base-content/60 px-3 -mt-3 mb-4 flex flex-col gap-0.5">
        <div className="flex justify-between items-center gap-2">
          <span className="flex-1 truncate">{cell(name1, focusRow, 1, onFocusClicked)}</span>
          <span className="shrink-0">{cell(fmt(focusRow, 1), focusRow, 1, onFocusClicked)}</span>
          <span className="shrink-0">{oppRow ? cell(fmt(oppRow, 1), oppRow, 1, onOppClicked) : ""}</span>
          <span className="flex-1 truncate text-right">{oppRow ? cell(name3, oppRow, 1, onOppClicked) : ""}</span>
        </div>
        <div className="flex justify-between items-center gap-2">
          <span className="flex-1 truncate">{cell(name2, focusRow, 2, onFocusClicked)}</span>
          <span className="shrink-0">{cell(fmt(focusRow, 2), focusRow, 2, onFocusClicked)}</span>
          <span className="shrink-0">{oppRow ? cell(fmt(oppRow, 2), oppRow, 2, onOppClicked) : ""}</span>
          <span className="flex-1 truncate text-right">{oppRow ? cell(name4, oppRow, 2, onOppClicked) : ""}</span>
        </div>
      </div>
    );
  };

  // ── Stat click → playlist ────────────────────────────────────────────────

  // Open a playlist in the right panel; on mobile also collapse the left panel
  // so the video/events get the full width.
  const openPlaylist = useCallback((pl) => {
    setActivePlaylist(pl);
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      setShowPanel(false);
    }
  }, []);

  const handleStatClicked = useCallback((events, stat, side) => {
    if (!events?.length) return;
    const firstMatch = matches[0];
    const focusIsP1 = isFocusTeamA(firstMatch, playerGuid);
    let playerName;
    if (side === 1) {
      const p = firstMatch?.isDoubles
        ? (focusIsP1 ? firstMatch.teamA : firstMatch.teamB)
        : (focusIsP1 ? firstMatch?.player1 : firstMatch?.player2);
      playerName = p ? (resolveDisplayName(p) || "Focus Player") : "Focus Player";
    } else {
      const p = firstMatch?.isDoubles
        ? (focusIsP1 ? firstMatch.teamB : firstMatch.teamA)
        : (focusIsP1 ? firstMatch?.player2 : firstMatch?.player1);
      playerName = p ? (resolveDisplayName(p) || "Opponent") : "Opponent";
    }
    const title = `${stat.label} \u2013 ${playerName}`;
    const psplStr = buildPlaylistPspl(title, events, matches);
    const data = compressToPspl(psplStr);
    openPlaylist({ data, name: title, comments: {}, firestoreId: null });
  }, [matches, playerGuid, openPlaylist]);

  // Generic click handler for the individual-breakdown sections below (a
  // stat there compares 2 named individuals, not focus/opposition).
  const makeIndividualStatClicked = useCallback((name1, name2) => (events, stat, side) => {
    if (!events?.length) return;
    const playerName = side === 1 ? name1 : name2;
    const title = `${stat.label} \u2013 ${playerName}`;
    const psplStr = buildPlaylistPspl(title, events, matches);
    const data = compressToPspl(psplStr);
    openPlaylist({ data, name: title, comments: {}, firestoreId: null });
  }, [matches, openPlaylist]);

  // ── PDF export (captures left column only) ───────────────────────────────

  const handleSavePdf = async () => {
    setGeneratingPdf(true);
    setIsPrinting(true);
    // The PDF is captured from the left report panel, so make sure it is shown.
    const panelWasHidden = !showPanel;
    if (panelWasHidden) setShowPanel(true);
    // Wait for React to re-render courts with print colours
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas-pro"),
        import("jspdf"),
      ]);

      const container = reportRef.current;

      // Expand the overflow container so all sections are fully laid out in the DOM
      const prevStyle = container.style.cssText;
      container.style.overflow = "visible";
      container.style.height = container.scrollHeight + "px";
      container.style.maxHeight = "none";
      // Brief pause for layout to settle after style change
      await new Promise((r) => setTimeout(r, 80));

      const sections = Array.from(container.querySelectorAll("[data-pdf-section]"));

      const MARGIN = 12;  // mm — all sides
      const GAP    = 5;   // mm — gap between grid cells
      const SCALE  = 2;   // capture resolution multiplier
      const COLS   = 3;   // court sections per row

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const printW = pageW - MARGIN * 2;
      const printH = pageH - MARGIN * 2;

      try {
        // Capture all sections up-front
        const canvases = [];
        for (const sec of sections) {
          canvases.push(await html2canvas(sec, {
            scale: SCALE,
            useCORS: true,
            logging: false,
            allowTaint: true,
          }));
        }

        let pageIndex = 0;

        // Place a single canvas centred on its own page (optionally at half width)
        const placeFullPage = (cv, halfWidth = false) => {
          if (pageIndex > 0) pdf.addPage();
          pageIndex++;
          const imgData = cv.toDataURL("image/png");
          const maxW = halfWidth ? printW / 2 : printW;
          const imgW = maxW;
          const imgH = (cv.height * imgW) / cv.width;
          if (imgH > printH) {
            const scale = printH / imgH;
            const scaledW = imgW * scale;
            pdf.addImage(imgData, "PNG", (pageW - scaledW) / 2, MARGIN, scaledW, printH);
          } else {
            pdf.addImage(imgData, "PNG", (pageW - imgW) / 2, MARGIN + (printH - imgH) / 2, imgW, imgH);
          }
        };

        // Title page (index 0) → half width, centred; stats page (index 1) → full width
        placeFullPage(canvases[0], true);
        if (canvases[1]) placeFullPage(canvases[1], false);

        // Court sections (index 2+) → 2-column grid, multiple rows per page
        const colW = (printW - GAP * (COLS - 1)) / COLS;
        let yPos = pageH; // force a new page on the very first row

        const courtCanvases = canvases.slice(2);
        for (let i = 0; i < courtCanvases.length; i += COLS) {
          const row = courtCanvases.slice(i, i + COLS);
          const rowH = Math.max(...row.map((cv) => (cv.height * colW) / cv.width));

          if (yPos + rowH > pageH - MARGIN) {
            pdf.addPage();
            pageIndex++;
            yPos = MARGIN;
          }

          const rowYPos = yPos;
          row.forEach((cv, colIdx) => {
            const x = MARGIN + colIdx * (colW + GAP);
            const imgH = (cv.height * colW) / cv.width;
            pdf.addImage(cv.toDataURL("image/png"), "PNG", x, rowYPos, colW, imgH);
          });

          yPos += rowH + GAP;
        }
      } finally {
        container.style.cssText = prevStyle;
      }

      const firstMatch = matches[0];
      const focusIsP1 = isFocusTeamA(firstMatch, playerGuid);
      const playerName = firstMatch?.isDoubles
        ? ((focusIsP1 ? firstMatch.teamA : firstMatch.teamB)?.name ?? "team")
        : (focusIsP1 ? firstMatch?.player1LastName : (firstMatch?.player2LastName ?? "match"));
      const fileName = matches.length === 1 ? `report-${playerName}.pdf` : `report-${matches.length}-matches.pdf`;
      pdf.save(fileName);
    } catch (err) {
      console.error("PDF generation failed", err);
      toast.error("Failed to generate PDF");
    } finally {
      setIsPrinting(false);
      setGeneratingPdf(false);
      if (panelWasHidden) setShowPanel(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col" style={{ height: "100dvh" }}>
      {/* ── Toolbar ── */}
      <div className="shrink-0 flex items-center gap-2 px-4 py-2 border-b border-base-300 bg-base-100">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>← Back</button>
        <button
          className={`btn btn-sm btn-ghost tooltip tooltip-bottom${showPanel ? " btn-active" : ""}`}
          data-tip={showPanel ? "Hide report panel" : "Show report panel"}
          onClick={() => setShowPanel((v) => !v)}
        >
          <ViewColumnsIcon className="size-6" />
          {/* <span className="text-xs ml-1 hidden sm:inline">Reports</span> */}
        </button>
        <h1 className="text-lg font-bold">Reports</h1>
        {matchGuids.length > 1 && (
          <span className="badge badge-primary">{matchGuids.length} matches</span>
        )}
        {loading && <span className="loading loading-spinner loading-sm" />}

        <div className="ml-auto flex items-center gap-1">
          <button
            className={`btn btn-sm btn-ghost tooltip tooltip-bottom${isNormalised ? " btn-active" : ""}`}
            data-tip="Normalise"
            onClick={() => setIsNormalised((v) => !v)}
          >
            {isNormalised ? <ArrowsUpDownIcon className="w-4 h-4" /> : <ArrowUpIcon className="w-4 h-4" />}
          </button>
          <button
            className={`btn btn-sm btn-ghost tooltip tooltip-bottom${showSource ? " btn-active" : ""}`}
            data-tip="Show source"
            onClick={() => setShowSource((v) => !v)}
          >
            <ArrowPathIcon className="w-4 h-4" />
          </button>
          <div className="dropdown dropdown-end dropdown-bottom">
            <button tabIndex={0} className="btn btn-sm btn-ghost tooltip tooltip-bottom" data-tip="Display mode">
              <ListBulletIcon className="w-4 h-4" />
              <span className="text-xs ml-1 hidden sm:inline">
                {DISPLAY_MODES.find((m) => m.value === displayMode)?.label}
              </span>
            </button>
            <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-box z-50 w-44 p-2 shadow">
              {DISPLAY_MODES.map((mode) => (
                <li key={mode.value}>
                  <button className={displayMode === mode.value ? "active" : ""} onClick={() => setDisplayMode(mode.value)}>
                    {mode.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <button
            className="btn btn-sm btn-primary gap-1"
            onClick={handleSavePdf}
            disabled={generatingPdf || matches.length === 0}
          >
            {generatingPdf ? <span className="loading loading-spinner loading-xs" /> : <DocumentArrowDownIcon className="w-4 h-4" />}
            PDF
          </button>
        </div>
      </div>

      {/* ── Two-column body ── */}
      <div ref={bodyRef} className="flex flex-1 overflow-hidden select-none">

        {/* Left column — report (resizable) */}
        {showPanel && (
        <>
        <div
          className="flex flex-col overflow-hidden shrink-0 border-r border-base-300"
          style={{ width: leftWidth }}
        >
          <div ref={reportRef} className="flex-1 overflow-y-auto">
            <div className="px-3 py-4 space-y-6">

              {/* Report header */}
              <div className="text-center border-b border-base-300 pb-4" data-pdf-section>
                <h2 className="text-xl font-bold mb-1">Tennis Stats Report</h2>
                {renderPlayerHeader()}
                <p className="text-xs text-base-content/50 mb-3">
                  {matchGuids.length} {matchGuids.length === 1 ? "match" : "matches"} &bull;{" "}
                  Generated {new Date().toLocaleDateString()}
                </p>
                {/* Match list */}
                {matches.length > 0 && (
                  <div className="text-left border-t border-base-300 pt-2 mt-1">
                    <button
                      className="text-xs font-semibold uppercase tracking-wide text-base-content/50 flex items-center gap-1 hover:text-base-content transition-colors"
                      onClick={() => setShowMatchList((v) => !v)}
                    >
                      <span>{showMatchList || isPrinting ? "▾" : "▸"}</span>
                      Matches included ({matches.length})
                    </button>
                    {(showMatchList || isPrinting) && (
                      <div className="mt-1 text-xs divide-y divide-base-300/40">
                        {matches.map((m, i) => {
                          const focusIsP1 = isFocusTeamA(m, playerGuid);
                          const opp = m.isDoubles
                            ? ((focusIsP1 ? m.teamB : m.teamA)?.name ?? "")
                            : (focusIsP1
                              ? `${m.player2FirstName ?? ""} ${m.player2LastName ?? ""}`.trim()
                              : `${m.player1FirstName ?? ""} ${m.player1LastName ?? ""}`.trim());
                          const date = m.matchDate ? new Date(m.matchDate).toLocaleDateString() : "—";
                          const tournament = m.tournament?.name ?? "—";
                          return (
                            <div key={m.guid ?? i} className="flex py-1 gap-2">
                              <div className="shrink-0 text-base-content/40 w-4">{i + 1}</div>
                              <div className="font-semibold" style={{ width: "35%" }}>{opp || "—"}</div>
                              <div className="flex flex-col min-w-0 flex-1">
                                <span className="truncate text-base-content/70">{tournament}</span>
                                <span className="text-base-content/50">{date}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Section 1: Match Statistics */}
              <section data-pdf-section>
                <h2 className="text-sm font-bold border-b border-base-300 pb-1 mb-3 uppercase tracking-wide">
                  Match Statistics
                </h2>
                {(() => {
                  const firstMatch = matches[0];
                  const isDoublesMatch = firstMatch?.isDoubles;
                  const focusIsP1 = firstMatch ? isFocusTeamA(firstMatch, playerGuid) : true;
                  const focusTeam = isDoublesMatch ? (focusIsP1 ? firstMatch.teamA : firstMatch.teamB) : null;
                  const oppTeam = isDoublesMatch ? (focusIsP1 ? firstMatch.teamB : firstMatch.teamA) : null;
                  const names = [
                    resolveDisplayName(focusTeam?.player1) || "Player 1",
                    resolveDisplayName(focusTeam?.player2) || "Player 2",
                    resolveDisplayName(oppTeam?.player1) || "Player 1",
                    resolveDisplayName(oppTeam?.player2) || "Player 2",
                  ];
                  const onFocusClicked = makeIndividualStatClicked(names[0], names[1]);
                  const onOppClicked = makeIndividualStatClicked(names[2], names[3]);
                  const byLabel = (bars) => new Map(bars.map((b) => [b.label, b]));
                  const focusByLabel = byLabel(focusIndividualBars);
                  const oppByLabel = byLabel(oppIndividualBars);
                  // These are inherently team-level (a rally/breakpoint/point
                  // is won or lost by the TEAM, not attributable to one
                  // player) — no per-player breakdown for them.
                  const NO_BREAKDOWN_CATEGORIES = new Set(["Rally Length", "Break Points"]);
                  const NO_BREAKDOWN_LABELS = new Set(["Total Points Won", "Total Pressure Points Won"]);

                  return statBars.length > 0
                    ? STAT_CATEGORIES.map((cat, i) => {
                        const bars = statBars.filter((sb) => sb.category === i);
                        if (bars.length === 0) return null;
                        const categoryAllowsBreakdown = isDoublesMatch && !NO_BREAKDOWN_CATEGORIES.has(cat);
                        return (
                          <div key={i}>
                            <div className="text-xs font-semibold my-2 text-center text-base-content/60 uppercase tracking-wide">
                              {cat}
                            </div>
                            {bars.map((sb, j) => (
                              <div key={j}>
                                <ComparisonBars stat={sb} onStatClicked={handleStatClicked} />
                                {categoryAllowsBreakdown &&
                                  !NO_BREAKDOWN_LABELS.has(sb.label) &&
                                  renderPlayerBreakdownRow(
                                    focusByLabel.get(sb.label),
                                    oppByLabel.get(sb.label),
                                    names,
                                    onFocusClicked,
                                    onOppClicked,
                                  )}
                              </div>
                            ))}
                          </div>
                        );
                      })
                    : !loading && (
                        <p className="text-base-content/50 text-xs text-center py-4">No stats available</p>
                      );
                })()}
              </section>

              {/* Doubles Analysis: formations, serve direction, return hand */}
              {matches[0]?.isDoubles && (
                <section data-pdf-section>
                  <div className="flex items-center justify-between border-b border-base-300 pb-1 mb-3">
                    <h2 className="text-sm font-bold uppercase tracking-wide">Doubles Analysis</h2>
                    <button
                      className="btn btn-xs btn-ghost gap-1"
                      title="Open AI report for these doubles matches"
                      onClick={() => navigate("/ai-report", { state: { matchGuids, playerGuid } })}
                    >
                      <SparklesIcon className="w-3.5 h-3.5" />
                      AI Report
                    </button>
                  </div>
                  <DoublesAnalysisTables
                    analysis={doublesAnalysis}
                    serveFormationMode={serveFormationMode}
                    onServeFormationModeChange={setServeFormationMode}
                    serveDirectionMode={serveDirectionMode}
                    onServeDirectionModeChange={setServeDirectionMode}
                    returnFormationMode={returnFormationMode}
                    onReturnFormationModeChange={setReturnFormationMode}
                    returnHandMode={returnHandMode}
                    onReturnHandModeChange={setReturnHandMode}
                  />
                </section>
              )}

              {/* Sections 2–18: Court Analysis */}
              <section>
                <h2 className="text-sm font-bold border-b border-base-300 pb-1 mb-3 uppercase tracking-wide">
                  Match Analysis
                </h2>
                <div className="flex flex-col gap-4">
                  {REPORT_SECTIONS.map((s) => (
                    <CourtSection
                      key={s.id}
                      title={`${s.id}. ${s.title}`}
                      filters={s.filters}
                      matches={matches}
                      playerGuid={playerGuid}
                      displayMode={displayMode}
                      isNormalised={isNormalised}
                      showSource={showSource}
                      onOpenPlaylist={openPlaylist}
                      printMode={isPrinting}
                    />
                  ))}
                </div>
              </section>

            </div>
          </div>
        </div>

        {/* Drag handle */}
        <div
          className="w-1 cursor-col-resize bg-base-300 hover:bg-primary active:bg-primary transition-colors shrink-0"
          onMouseDown={onDragStart}
        />
        </>
        )}

        {/* Right column — PlaylistViewer or placeholder */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {activePlaylist ? (
            <PlaylistViewer
              playlist={activePlaylist}
              onBack={() => setActivePlaylist(null)}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-base-content/20 text-sm flex-col gap-2">
              <VideoCameraIcon className="w-10 h-10" />
              <span>Click <VideoCameraIcon className="w-4 h-4 inline" /> on a court section to view its events</span>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default GeneralReport;
